/* Copyright (c) 2020-present CloudIO Inc */

import {
  DBRow,
  FieldType,
  IoDatasourceAttributes,
  NormalizedAttributeType,
} from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import {
  AliasAndEmbeddedPageIdValue,
  InvokeAttributeChooserProp,
} from 'componentregistry';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { pageAtom } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { isEmpty } from 'utils/isEmpty';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';
import useAppStore from 'store/useAppStore';

interface AttributeOption extends Option {
  value: string;
  label: string;
  dataType: NormalizedAttributeType;
}

export type OnChangeHandler<T extends Option = Option> = (
  val: string,
  option: T | null,
) => void;

export function InvokeChooser({
  ds,
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  types = 'ALL',
  value,
  autoFocus = false,
  dsAppUid,
  disabled = false,
}: {
  autoFocus?: boolean;
  ds: string;
  appUid: string;
  helpText?: string;
  label: string;
  onChange: OnChangeHandler<AttributeOption>;
  required?: boolean;
  testid?: string;
  types: FieldType[] | 'ALL';
  value: string;
  dsAppUid: string | undefined;
  disabled?: boolean;
}) {
  const { query, records, isBusy } = useAppStore<IoDatasourceAttributes>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoDatasourceAttributes',
    alias: 'IoDatasourceAttributes-chooser-invokeDS',
  });

  useEffect(() => {
    if (ds) {
      query({
        props: {
          filter: [{ datasourceUid: { is: ds } }, { appUid: { is: appUid } }],
          limit: 2000,
        },
      });
    }
  }, [appUid, ds, query]);

  const handleChange: OnChangeHandler = (attr, option) => {
    if (attr !== value) {
      onChange(attr, option as AttributeOption);
    }
  };

  const options = (
    Object.values(records) as DBRow<IoDatasourceAttributes>[]
  ).map(({ attributeCode, attributeName }) => ({
    value: attributeCode,
    label: attributeName,
  }));

  return (
    <VirtualizedSelectInput
      data-testid={testid}
      variant="standard"
      autoFocus={autoFocus}
      fullWidth
      value={value}
      options={options}
      onChange={handleChange}
      label={label}
      startIcon={isBusy ? <WaveDots /> : null}
      disabled={isBusy || disabled}
      placeholder={isBusy ? 'Loading ...' : 'Choose attribute'}
      required={required}
      helpText={helpText}
    />
  );
}

function AttributeChooserProperty({
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  alias,
  aliasPageId,
  onChange,
  appUid,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<InvokeAttributeChooserProp, string> & {
  aliasPageId: string;
}) {
  let helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange: OnChangeHandler<AttributeOption> = (
    attr,
    option: AttributeOption | null,
  ) => {
    if (attr !== value) {
      onChange(property, attr);
    }
  };
  const page = platform.getPage(appUid, pageId);
  const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
  const types = def.getAttributeDataTypes(itemWithDefaults);

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  if (isEmpty(helpText) && Array.isArray(types)) {
    const typesString = types.join(', ');
    helpText = `Allowed types: ${typesString}`;
  }

  if (isEmpty(alias)) {
    return <>Missing Store</>;
  }

  return (
    <InvokeChooser
      disabled={isViewOnlyUser}
      ds={alias}
      appUid={appUid}
      helpText={helpText}
      label={def.label}
      onChange={handleChange}
      required={required}
      testid={property}
      types={types}
      value={value}
      dsAppUid={appUid}
    />
  );
}

function InvokeAttributeChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  hmr,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<InvokeAttributeChooserProp, string>) {
  const { aliasType, getAlias } = def;
  let aliasAndEmbeddedPageIdValue: AliasAndEmbeddedPageIdValue | null = null;
  if (getAlias) {
    const page = platform.getter(pageAtom, { params: { appUid, pageId } });
    if (page) {
      const item = itemWithDefaultValues(page[activeItemId]);
      aliasAndEmbeddedPageIdValue = getAlias(page, item, {
        pageId,
        appUid,
        itemId: activeItemId,
        mgr,
        platform,
      });
    }
  } else {
    const _aliasType =
      typeof aliasType === 'function'
        ? aliasType({ mgr, appUid, pageId, itemId: activeItemId, platform })
        : aliasType;
    aliasAndEmbeddedPageIdValue = platform.getDataSourceAlias(
      appUid,
      pageId,
      activeItemId,
      _aliasType,
    );
  }
  const [, setState] = useState([]);
  useEffect(() => {
    if (def.attributeRefreshCallback) {
      def.attributeRefreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);

  return (
    <AttributeChooserProperty
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      hmr={hmr}
      activeItemId={activeItemId}
      def={def}
      key={`${property}-${activeItemId}`}
      pageId={pageId}
      property={property}
      mgr={mgr}
      value={value as string}
      alias={aliasAndEmbeddedPageIdValue?.alias}
      aliasPageId={aliasAndEmbeddedPageIdValue?.embeddedPageId ?? pageId}
      onChange={onChange}
      platform={platform}
    />
  );
}

export default memo(InvokeAttributeChooserProperty);
